import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Cloud, FolderPlus, Check, Settings } from 'lucide-react';
import { useDrive } from '@/hooks/useDrive';

const DriveSetup = () => {
  const { connected, folderConfigured, folderName, connectDrive, getFolders, createFolder, saveSettings } = useDrive();
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadFolders = useCallback(async () => {
    const folderList = await getFolders();
    setFolders(folderList);
  }, [getFolders]);

  useEffect(() => {
    if (connected && dialogOpen) {
      loadFolders();
    }
  }, [connected, dialogOpen, loadFolders]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    const folder = await createFolder(newFolderName);
    if (folder) {
      setFolders([...folders, folder]);
      setSelectedFolder(folder.id);
      setNewFolderName('');
      setShowNewFolder(false);
    }
  };

  const handleSave = async () => {
    if (!selectedFolder) return;
    
    const folder = folders.find(f => f.id === selectedFolder);
    if (folder) {
      await saveSettings(folder.id, folder.name);
      setDialogOpen(false);
    }
  };

  if (!connected) {
    return (
      <Button
        onClick={connectDrive}
        variant="outline"
        className="gap-2"
        data-testid="connect-drive-btn"
      >
        <Cloud className="w-4 h-4" />
        Connect Google Drive
      </Button>
    );
  }

  if (!folderConfigured) {
    return (
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="gap-2" data-testid="setup-drive-folder-btn">
            <Settings className="w-4 h-4" />
            Setup Drive Folder
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Google Drive Folder</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Choose existing folder</Label>
              <Select value={selectedFolder} onValueChange={setSelectedFolder}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select a folder" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {folders.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="text-center text-sm text-gray-500">OR</div>

            {!showNewFolder ? (
              <Button
                variant="outline"
                onClick={() => setShowNewFolder(true)}
                className="w-full gap-2"
              >
                <FolderPlus className="w-4 h-4" />
                Create New Folder
              </Button>
            ) : (
              <div className="space-y-2">
                <Label>New folder name</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="RA License Documents"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                  />
                  <Button onClick={handleCreateFolder}>Create</Button>
                </div>
              </div>
            )}

            <Button
              onClick={handleSave}
              disabled={!selectedFolder}
              className="w-full"
            >
              Save Configuration
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
      <Check className="w-4 h-4" />
      <span>Saving to: <strong>{folderName}</strong></span>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" className="h-6 px-2">
            Change
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Google Drive Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Choose folder</Label>
              <Select value={selectedFolder} onValueChange={setSelectedFolder}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select a folder" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {folders.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSave} disabled={!selectedFolder} className="w-full">
              Update Folder
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriveSetup;
